import type { QueryClient } from '@tanstack/react-query'

import {
  getGetBrokersMembersMemberIdEnrollmentsIdWorkflowsQueryKey,
  getGetBrokersTaskWorkflowsIdQueryKey,
} from 'utils/web_api'
import type { BrokersTask } from 'utils/web_api.schemas'

/**
 * Options for invalidating workflow-related queries
 */
export interface InvalidateWorkflowQueriesOptions {
  /** Workflow ID - required for invalidating individual workflow queries */
  workflowId?: string
  /** Enrollment ID - required for invalidating enrollment-specific workflow list */
  enrollmentId?: string
  /** Member ID - required for invalidating enrollment-specific workflow list */
  memberId?: string
  /** Task object - used as fallback to extract workflowId and memberId if not provided */
  task?: BrokersTask
}

/**
 * Invalidates all workflow-related queries after a workflow or task action.
 *
 * This function handles invalidating:
 * 1. Individual workflow query (all variants with different parameters)
 * 2. Enrollment-specific workflow list query (BrokersWorkflowSummary[])
 *
 * It uses fallbacks from the task object if IDs aren't explicitly provided,
 * making it safe to call even when some IDs are missing.
 *
 * @param queryClient - React Query client instance
 * @param options - Options for which queries to invalidate
 */
export function invalidateWorkflowQueries (
  queryClient: QueryClient,
  options: InvalidateWorkflowQueriesOptions
): void {
  const {
    workflowId: providedWorkflowId,
    enrollmentId,
    memberId: providedMemberId,
    task,
  } = options

  // Determine effective workflow ID (use provided, fallback to task.workflow_id)
  const workflowId = providedWorkflowId || task?.workflow_id

  // Determine effective member ID (use provided, fallback to task.member_id)
  const memberId = providedMemberId || task?.member_id

  // Invalidate individual workflow query (all variants)
  // This catches queries with different parameters like evaluate_readiness
  if (workflowId) {
    queryClient.invalidateQueries({
      queryKey: getGetBrokersTaskWorkflowsIdQueryKey(workflowId),
    })
  }

  // Invalidate enrollment-specific workflow list query
  // This ensures the BrokersWorkflowSummary[] list is updated
  if (enrollmentId && memberId) {
    queryClient.invalidateQueries({
      queryKey: getGetBrokersMembersMemberIdEnrollmentsIdWorkflowsQueryKey(
        memberId,
        enrollmentId
      ),
    })
  }
}
